import { useEffect, useRef, useState } from 'react';
import { ApiError, apiFetch, v1Path } from '../../lib/api';
import type { WorkItemFull } from '../../lib/work-types';
import { cn } from '../../lib/utils';
import { useOverlays } from './overlay-context';
import { Input } from '../ui/input';

type Props = {
  projectId: string;
  item: WorkItemFull;
  onUpdated: (item: WorkItemFull) => void;
  readOnly?: boolean;
  size?: 'default' | 'large';
};

export default function TitleAutosave({ projectId, item, onUpdated, readOnly, size = 'default' }: Props) {
  const { toast } = useOverlays();
  const [title, setTitle] = useState(item.title);
  const savedTitle = useRef(item.title);
  const versionRef = useRef(item.version);
  const dirtyRef = useRef(false);
  const flushRef = useRef<() => Promise<void>>(async () => undefined);

  useEffect(() => {
    setTitle(item.title);
    savedTitle.current = item.title;
    versionRef.current = item.version;
    dirtyRef.current = false;
  }, [item.id, item.title, item.version]);

  async function persist(nextTitle: string): Promise<void> {
    const trimmed = nextTitle.trim();
    if (!trimmed) {
      setTitle(savedTitle.current);
      dirtyRef.current = false;
      return;
    }
    if (trimmed === savedTitle.current) {
      dirtyRef.current = false;
      return;
    }
    try {
      const dto = await apiFetch<WorkItemFull>(
        v1Path(`/projects/${projectId}/work-items/${item.id}`),
        { method: 'PATCH', body: JSON.stringify({ version: versionRef.current, title: trimmed }) },
      );
      savedTitle.current = dto.title;
      versionRef.current = dto.version;
      dirtyRef.current = false;
      setTitle(dto.title);
      onUpdated(dto);
      window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        try {
          const fresh = await apiFetch<WorkItemFull>(
            v1Path(`/projects/${projectId}/work-items/${item.id}`),
          );
          versionRef.current = fresh.version;
          const retry = await apiFetch<WorkItemFull>(
            v1Path(`/projects/${projectId}/work-items/${item.id}`),
            { method: 'PATCH', body: JSON.stringify({ version: fresh.version, title: trimmed }) },
          );
          savedTitle.current = retry.title;
          versionRef.current = retry.version;
          dirtyRef.current = false;
          setTitle(retry.title);
          onUpdated(retry);
          window.dispatchEvent(new CustomEvent('bb:work-items-changed'));
          return;
        } catch {
          toast('Could not save title. Keep editing and try again.');
          return;
        }
      }
      toast(err instanceof Error ? err.message : 'Failed to save title');
    }
  }

  flushRef.current = async () => {
    if (dirtyRef.current) await persist(title);
  };

  useEffect(() => {
    if (readOnly) return;
    const handle = window.setTimeout(() => {
      if (dirtyRef.current) void persist(title);
    }, 800);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, readOnly]);

  useEffect(() => {
    function onBeforeUnload(event: BeforeUnloadEvent) {
      if (dirtyRef.current) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      void flushRef.current();
    };
  }, []);

  if (readOnly) {
    return (
      <h1
        data-shortcut="title"
        tabIndex={-1}
        className={size === 'large' ? 'text-2xl font-semibold tracking-tight' : 'text-lg font-semibold tracking-tight'}
      >
        {item.title}
      </h1>
    );
  }

  return (
    <Input
      data-shortcut="title"
      value={title}
      onChange={(e) => {
        setTitle(e.target.value);
        dirtyRef.current = e.target.value.trim() !== savedTitle.current;
      }}
      onBlur={() => {
        if (!title.trim()) {
          setTitle(savedTitle.current);
          dirtyRef.current = false;
        }
      }}
      aria-label="Work item title"
      className={cn(
        'h-auto border-transparent px-2 py-1 font-semibold hover:border-input focus:border-input',
        size === 'large' ? '-ml-2 text-2xl tracking-tight md:text-2xl' : 'text-lg',
      )}
    />
  );
}
